import React, { useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import EditWorkflowModal from '../components/pages/admin/workflows/EditWorkflowModal';
import type { Language } from '../config/localization';
import { stripUrlQueryKey } from '../utils/urlSanitizer';
import { buildExportUrls } from '../utils/workflowUrlExport';
import { normalizeOutgoingImagePolicy } from '../utils/outgoingImagePolicy';
import { stripMaskConfigForInputFilesLogic } from '../components/pages/admin/workflows/sections/SlotsInputMediaSection';
import type {
  WorkflowCategory,
  WidgetType,
  WorkflowWidget,
  WorkflowWithTranslations as BaseWorkflowWithTranslations,
  FormData,
  TileConfig,
  InputContentConfig,
  DynamicWidgetConfig,
  WorkflowSettings
} from '../components/pages/admin/workflows/sections';

// Extended WorkflowWithTranslations for API response (includes extra fields for editing)
export interface WorkflowWithTranslations extends BaseWorkflowWithTranslations {
  title?: string;
  default_prompt_id?: number | null;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  prompt_char_limit?: number;
  is_nsfw?: boolean;
  tile_config?: TileConfig | string | null;
  input_content_config?: InputContentConfig | string | null;
  dynamic_widget?: DynamicWidgetConfig | string | null;
  settings?: WorkflowSettings | string | null;
  multi_task_type?: string | null;
}

// Re-export types for consumers
export type { WorkflowCategory, WidgetType, WorkflowWidget, TileConfig, InputContentConfig, DynamicWidgetConfig };
export type EditFormData = FormData;

// Initial form data
const getInitialFormData = (): EditFormData => ({
  visual_id: '',
  title: '',
  description_ru: '',
  description_en: '',
  category: 'Image',
  action_button_type: 'generate',
  input_files_logic: 'all_per_request',
  is_available_for_all: false,
  is_nsfw: false,
  pricing_formula: '100',
  prompt_char_limit: 5000,
  min_image_width: null,
  min_image_height: null,
  max_image_width: null,
  max_image_height: null,
  tile_config: {
    displayType: null,
    imageUrl: null,
    imageUrl2: null,
    videoUrl: null,
    comparisonMode: 'slider',
    autoSwitchDelay: 2000,
    slideshowImages: [],
    slideshowDelay: 1000,
    slideshowTransition: 'fade'
  },
  input_content_config: null,
  dynamic_widget: null,
  settings: null,
  display_status: 'active',
  multi_task_type: null
});

// Hook options
export interface UseWorkflowEditorOptions {
  onSaveSuccess?: (workflowId: number, updatedData: Partial<WorkflowWithTranslations>) => void;
  onError?: (error: string) => void;
  onExportSuccess?: (message: string) => void;
}

// Hook return type
export interface UseWorkflowEditorReturn {
  // State
  isOpen: boolean;
  workflow: WorkflowWithTranslations | null;
  widgets: WorkflowWidget[];
  widgetTypes: WidgetType[];
  formData: EditFormData;
  loading: boolean;
  saving: boolean;
  workflowId: number | null;

  // Operations
  open: (workflowId: number) => Promise<void>;
  close: () => void;
  save: () => Promise<boolean>;

  // Form handlers
  updateField: (field: keyof EditFormData | string, value: EditFormData[keyof EditFormData]) => void;
  toggleWidget: (widgetTypeId: number, isEnabled: boolean) => void;
  updateDisplayOrder: (widgetTypeId: number, order: number) => void;
  updateWidgetConfig: (widgetTypeId: number, configData: string) => void;

  // Render helper
  renderModal: (selectedLanguage: Language) => React.ReactNode;
}

export function useWorkflowEditor(options?: UseWorkflowEditorOptions): UseWorkflowEditorReturn {
  const { getToken } = useAuth();

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowWithTranslations | null>(null);
  const [widgets, setWidgets] = useState<WorkflowWidget[]>([]);
  const [widgetTypes, setWidgetTypes] = useState<WidgetType[]>([]);
  const [formData, setFormData] = useState<EditFormData>(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  // Load widget types
  const loadWidgetTypes = useCallback(async () => {
    try {
      const token = await getToken({ template: 'withmail' });

      const response = await fetch('/api/admin/widgets/types?active_only=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setWidgetTypes(data.widgetTypes || []);
        return data.widgetTypes || [];
      }
    } catch (error) {
      console.error('Error loading widget types:', error);
    }
    return [];
  }, [getToken]);

  // Open modal and load workflow data
  const open = useCallback(async (id: number) => {
    setLoading(true);
    setWorkflowId(id);

    try {
      const token = await getToken({ template: 'withmail' });

      // Load widget types if not loaded
      let currentWidgetTypes = widgetTypes;
      if (widgetTypes.length === 0) {
        currentWidgetTypes = await loadWidgetTypes();
      }

      const response = await fetch(`/api/admin/workflows/${id}/widgets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWidgets(data.widgets || []);
        setWorkflow(data.workflow);

        const wf = data.workflow;

        // Parse tile_config
        let parsedTileConfig = {
          displayType: null as 'single_image' | 'image_pair' | 'single_video' | 'slideshow' | null,
          imageUrl: null as string | null,
          imageUrl2: null as string | null,
          videoUrl: null as string | null,
          comparisonMode: 'slider' as 'slider' | 'auto_switch',
          autoSwitchDelay: 2000,
          slideshowImages: [] as string[],
          slideshowDelay: 1000,
          slideshowTransition: 'fade' as 'instant' | 'fade' | 'slide'
        };

        if (wf.tile_config) {
          try {
            const parsed = typeof wf.tile_config === 'string'
              ? JSON.parse(wf.tile_config)
              : wf.tile_config;
            parsedTileConfig = {
              displayType: parsed.displayType || null,
              imageUrl: parsed.imageUrl || null,
              imageUrl2: parsed.imageUrl2 || null,
              videoUrl: parsed.videoUrl || null,
              comparisonMode: parsed.comparisonMode || 'slider',
              autoSwitchDelay: parsed.autoSwitchDelay || 2000,
              slideshowImages: parsed.slideshowImages || [],
              slideshowDelay: parsed.slideshowDelay || 1000,
              slideshowTransition: parsed.slideshowTransition || 'fade'
            };
          } catch (e) {
            console.warn('Failed to parse tile_config:', e);
          }
        }

        // Parse input_content_config
        let parsedInputContentConfig = null;
        if (wf.input_content_config) {
          try {
            parsedInputContentConfig = typeof wf.input_content_config === 'string'
              ? JSON.parse(wf.input_content_config)
              : wf.input_content_config;
          } catch (e) {
            console.warn('Failed to parse input_content_config:', e);
          }
        }

        // Parse dynamic_widget
        let parsedDynamicWidget = null;
        if (wf.dynamic_widget) {
          try {
            parsedDynamicWidget = typeof wf.dynamic_widget === 'string'
              ? JSON.parse(wf.dynamic_widget)
              : wf.dynamic_widget;
          } catch (e) {
            console.warn('Failed to parse dynamic_widget:', e);
          }
        }

        // Parse settings JSON
        let parsedSettings: WorkflowSettings | null = null;
        if (wf.settings) {
          try {
            parsedSettings = typeof wf.settings === 'string'
              ? JSON.parse(wf.settings)
              : wf.settings;
          } catch (e) {
            console.warn('Failed to parse settings:', e);
          }
        }

        setFormData({
          visual_id: wf.visual_id || wf.id?.toString() || '',
          title: wf.title || wf.url || '',
          description_ru: wf.description_translations?.ru || '',
          description_en: wf.description_translations?.en || '',
          category: wf.category || 'Image',
          action_button_type: wf.action_button_type || 'generate',
          input_files_logic: wf.input_files_logic || 'all_per_request',
          is_available_for_all: wf.is_available_for_all || false,
          is_nsfw: wf.is_nsfw || false,
          pricing_formula: wf.pricing_formula || '100',
          prompt_char_limit: wf.prompt_char_limit || 5000,
          min_image_width: wf.min_image_width || null,
          min_image_height: wf.min_image_height || null,
          max_image_width: wf.max_image_width || null,
          max_image_height: wf.max_image_height || null,
          tile_config: parsedTileConfig,
          input_content_config: parsedInputContentConfig,
          dynamic_widget: parsedDynamicWidget,
          settings: parsedSettings,
          display_status: wf.display_status || 'active',
          multi_task_type: wf.multi_task_type || null
        });

        // Load workflow's prompt categories
        try {
          const categoriesResponse = await fetch(`/api/admin/workflows/${id}/categories`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (categoriesResponse.ok) {
            const categoriesData = await categoriesResponse.json();
            setSelectedCategoryIds((categoriesData.categories || []).map((cat: { id: number }) => cat.id));
          }
        } catch (e) {
          console.warn('Failed to load workflow categories:', e);
          setSelectedCategoryIds([]);
        }

        // Load workflow's tags
        try {
          const tagsResponse = await fetch(`/api/admin/workflows/${id}/tags`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (tagsResponse.ok) {
            const tagsData = await tagsResponse.json();
            setSelectedTagIds((tagsData.tags || []).map((tag: { id: number }) => tag.id));
          }
        } catch (e) {
          console.warn('Failed to load workflow tags:', e);
          setSelectedTagIds([]);
        }

        setIsOpen(true);
      } else {
        options?.onError?.('Failed to load workflow');
      }
    } catch (error) {
      console.error('Error loading workflow for edit:', error);
      options?.onError?.('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  }, [getToken, widgetTypes, loadWidgetTypes, options]);

  // Close modal
  const close = useCallback(() => {
    setIsOpen(false);
    setWorkflowId(null);
    setWorkflow(null);
    setWidgets([]);
    setFormData(getInitialFormData());
    setSelectedCategoryIds([]);
    setSelectedTagIds([]);
  }, []);

  // Save workflow
  const save = useCallback(async (): Promise<boolean> => {
    if (!workflowId) return false;

    setSaving(true);
    try {
      const normalizedPolicy = normalizeOutgoingImagePolicy(formData.settings?.outgoing_image_policy);
      const normalizedSettings = formData.settings
        ? { ...formData.settings, ...(normalizedPolicy !== undefined ? { outgoing_image_policy: normalizedPolicy } : {}) }
        : formData.settings;
      const token = await getToken({ template: 'withmail' });

      const tileConfigValue = formData.tile_config?.displayType
        ? formData.tile_config
        : null;

      const inputContentConfigValue = formData.input_content_config &&
        (formData.input_content_config.images ||
         formData.input_content_config.video ||
         formData.input_content_config.audio ||
         formData.input_content_config.referenceImages ||
         formData.input_content_config.referenceVideos ||
         formData.input_content_config.referenceAudio)
        ? formData.input_content_config
        : null;

      const dynamicWidgetValue = formData.dynamic_widget?.name
        ? formData.dynamic_widget
        : null;

      // Clean pipeline steps: filter out output mappings with empty source/target
      const cleanedSettings = normalizedSettings?.steps
        ? {
            ...normalizedSettings,
            steps: normalizedSettings.steps.map((step: any) => ({
              ...step,
              output_mappings: (step.output_mappings || []).filter(
                (m: any) => m.source && m.target
              )
            }))
          }
        : normalizedSettings;

      const workflowPayload = {
        visual_id: formData.visual_id,
        title: formData.title,
        description_translations: {
          ...(formData.description_ru && { ru: formData.description_ru }),
          ...(formData.description_en && { en: formData.description_en })
        },
        category: formData.category,
        action_button_type: formData.action_button_type,
        input_files_logic: formData.input_files_logic,
        is_available_for_all: formData.is_available_for_all,
        is_nsfw: formData.is_nsfw,
        pricing_formula: formData.pricing_formula || '100',
        prompt_char_limit: formData.prompt_char_limit || 5000,
        min_image_width: formData.min_image_width,
        min_image_height: formData.min_image_height,
        max_image_width: formData.max_image_width,
        max_image_height: formData.max_image_height,
        tile_config: tileConfigValue,
        input_content_config: inputContentConfigValue,
        dynamic_widget: dynamicWidgetValue,
        settings: cleanedSettings,
        display_status: formData.display_status,
        multi_task_type: formData.multi_task_type
      };

      const workflowResponse = await fetch(`/api/admin/workflows/${workflowId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowPayload)
      });

      if (!workflowResponse.ok) {
        const errorData = await workflowResponse.json();
        options?.onError?.(errorData.error || 'Failed to update workflow');
        return false;
      }

      // Update widgets configuration. Normalize away inpainting-mask config when the
      // workflow is single_per_request — the Slots section's in-place cleanup only fires
      // while that section is mounted, so an admin who flips input_files_logic on the
      // General tab and saves would otherwise persist a mask that single_per_request bills
      // as its own request. This is the single save chokepoint, so the persisted config
      // stays consistent no matter which section was open.
      const widgetsData = widgets.map(widget => ({
        widget_type_id: widget.widget_type_id,
        is_enabled: widget.is_enabled,
        display_order: widget.display_order,
        config_data:
          stripMaskConfigForInputFilesLogic(widget.config_data, formData.input_files_logic) || null
      }));

      const widgetsResponse = await fetch(`/api/admin/workflows/${workflowId}/widgets`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ widgets: widgetsData })
      });

      if (!widgetsResponse.ok) {
        options?.onError?.('Failed to update widgets');
        return false;
      }

      // Save prompt categories
      const categoriesResponse = await fetch(`/api/admin/workflows/${workflowId}/categories`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category_ids: selectedCategoryIds })
      });

      if (!categoriesResponse.ok) {
        options?.onError?.('Failed to update prompt categories');
        return false;
      }

      // Save tags
      const tagsResponse = await fetch(`/api/admin/workflows/${workflowId}/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tag_ids: selectedTagIds })
      });

      if (!tagsResponse.ok) {
        options?.onError?.('Failed to update tags');
        return false;
      }

      // Callback with updated data
      options?.onSaveSuccess?.(workflowId, {
        title: formData.title,
        description_translations: {
          ...(formData.description_ru && { ru: formData.description_ru }),
          ...(formData.description_en && { en: formData.description_en })
        },
        category: formData.category,
        action_button_type: formData.action_button_type,
        pricing_formula: formData.pricing_formula
      });

      close();
      return true;
    } catch (error) {
      console.error('Error saving workflow:', error);
      options?.onError?.('Failed to save workflow');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workflowId, formData, widgets, selectedCategoryIds, selectedTagIds, getToken, options, close]);

  // Update form field
  const updateField = useCallback((field: keyof EditFormData | string, value: EditFormData[keyof EditFormData]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Toggle widget
  const toggleWidget = useCallback((widgetTypeId: number, isEnabled: boolean) => {
    setWidgets(prev => {
      const existingWidget = prev.find(w => w.widget_type_id === widgetTypeId);

      if (existingWidget) {
        return prev.map(w =>
          w.widget_type_id === widgetTypeId
            ? { ...w, is_enabled: isEnabled }
            : w
        );
      } else {
        const widgetType = widgetTypes.find(wt => wt.id === widgetTypeId);
        if (!widgetType) return prev;

        const newWidget: WorkflowWidget = {
          id: Date.now(),
          workflow_id: workflowId!,
          widget_type_id: widgetTypeId,
          is_enabled: true,
          display_order: prev.length,
          widget_type: widgetType
        };

        return [...prev, newWidget];
      }
    });
  }, [widgetTypes, workflowId]);

  // Update display order
  const updateDisplayOrder = useCallback((widgetTypeId: number, order: number) => {
    setWidgets(prev => prev.map(w =>
      w.widget_type_id === widgetTypeId
        ? { ...w, display_order: order }
        : w
    ));
  }, []);

  // Update widget config_data
  const updateWidgetConfig = useCallback((widgetTypeId: number, configData: string) => {
    setWidgets(prev => prev.map(w =>
      w.widget_type_id === widgetTypeId
        ? { ...w, config_data: configData }
        : w
    ));
  }, []);

  // Wrapper for save that returns void for modal compatibility
  const handleSaveForModal = useCallback(async (): Promise<void> => {
    await save();
  }, [save]);

  // Wrapper for updateField to match modal's expected signature
  const handleFormDataChange = useCallback((field: string, value: unknown): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Export single workflow
  const exportWorkflow = useCallback(async (id: number): Promise<void> => {
    try {
      const token = await getToken({ template: 'withmail' });

      const promptsResponse = await fetch('/api/admin/prompts/workflow', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!promptsResponse.ok) {
        throw new Error(`Failed to load workflow prompts (HTTP ${promptsResponse.status})`);
      }
      const promptsData = await promptsResponse.json();
      if (!Array.isArray(promptsData?.prompts)) {
        throw new Error('Workflow prompt catalog response is malformed');
      }
      const workflowPrompts = promptsData.prompts;

      // Fetch workflow widgets
      const widgetResponse = await fetch(`/api/admin/workflows/${id}/widgets`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let widgetsData: any[] = [];
      let workflowData: any = null;
      if (widgetResponse.ok) {
        const data = await widgetResponse.json();
        widgetsData = (data.widgets || []).map((widget: any) => ({
          widget_type_id: widget.widget_type_id,
          is_enabled: widget.is_enabled,
          display_order: widget.display_order,
          config_data: widget.config_data
        }));
        workflowData = data.workflow;
      }

      // Fetch workflow URLs. Endpoint data is required for a portable export,
      // so do not download a successful-looking file when this request fails.
      const urlsResponse = await fetch(`/api/admin/workflows/${id}/urls`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!urlsResponse.ok) {
        throw new Error(`Failed to load workflow URLs (HTTP ${urlsResponse.status})`);
      }
      const urlsData = await urlsResponse.json();
      const urls = buildExportUrls(urlsData.urls || [], workflowPrompts);

      // Fetch workflow tags
      let tags: any[] = [];
      try {
        const tagsResponse = await fetch(`/api/admin/workflows/${id}/tags`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (tagsResponse.ok) {
          const tagsData = await tagsResponse.json();
          tags = (tagsData.tags || []).map((tag: any) => ({
            name_en: tag.name_en,
            name_ru: tag.name_ru,
            display_order: tag.display_order
          }));
        }
      } catch (error) {
        console.error('Error fetching workflow tags:', error);
      }

      // Build export data using current formData and workflow data
      const wf = workflowData || workflow;
      const exportWorkflowData = {
        title: formData.title || wf?.title || wf?.url,
        visual_id: formData.visual_id || wf?.visual_id,
        description_translations: {
          ...(formData.description_ru && { ru: formData.description_ru }),
          ...(formData.description_en && { en: formData.description_en })
        },
        category: formData.category || wf?.category,
        action_button_type: formData.action_button_type || wf?.action_button_type,
        input_files_logic: formData.input_files_logic || wf?.input_files_logic,
        is_available_for_all: formData.is_available_for_all ?? wf?.is_available_for_all,
        // Multi opt-in tag. MUST be exported here too — dropping it makes the
        // workflow silently vanish from /multi after an import on another
        // environment (the import create-branch sends `?? null`). Same invariant
        // as the bulk export in AdminWorkflowsPage and workflowExport.ts.
        multi_task_type: formData.multi_task_type ?? wf?.multi_task_type ?? null,
        // Pricing and limits
        pricing_formula: formData.pricing_formula || wf?.pricing_formula,
        prompt_char_limit: formData.prompt_char_limit || wf?.prompt_char_limit,
        // Image size constraints
        min_image_width: formData.min_image_width ?? wf?.min_image_width,
        min_image_height: formData.min_image_height ?? wf?.min_image_height,
        max_image_width: formData.max_image_width ?? wf?.max_image_width,
        max_image_height: formData.max_image_height ?? wf?.max_image_height,
        // Tile and content config
        tile_config: formData.tile_config?.displayType ? formData.tile_config : wf?.tile_config,
        input_content_config: formData.input_content_config || wf?.input_content_config,
        // Dynamic widget config
        dynamic_widget: formData.dynamic_widget?.name ? formData.dynamic_widget : wf?.dynamic_widget,
        // Settings (pipeline steps, enhance config) — keep api_key in steps for cross-environment transfer, strip ?key= from URLs
        settings: (() => {
          const s = formData.settings || wf?.settings;
          if (!s) return undefined;
          try {
            const settingsCopy = typeof s === 'string' ? JSON.parse(s) : { ...s };
            if (settingsCopy.steps && Array.isArray(settingsCopy.steps)) {
              settingsCopy.steps = settingsCopy.steps.map((step: any) => {
                const safeStep = { ...step };
                if (safeStep.url) {
                  safeStep.url = stripUrlQueryKey(safeStep.url);
                }
                if (safeStep.workflow_url) {
                  safeStep.workflow_url = stripUrlQueryKey(safeStep.workflow_url);
                }
                if (safeStep.output_mappings) {
                  safeStep.output_mappings = safeStep.output_mappings.filter(
                    (m: any) => m.source && m.target
                  );
                }
                return safeStep;
              });
            }
            return settingsCopy;
          } catch {
            // On parse failure for strings, drop settings entirely.
            // For objects, still strip ?key= from URLs.
            if (typeof s === 'string') return undefined;
            try {
              if (s?.steps && Array.isArray(s.steps)) {
                return { ...s, steps: s.steps.map((step: any) => {
                  const rest = { ...step };
                  if (rest.url) rest.url = stripUrlQueryKey(rest.url);
                  if (rest.workflow_url) rest.workflow_url = stripUrlQueryKey(rest.workflow_url);
                  return rest;
                }) };
              }
              return { ...s };
            } catch { /* fall through */ }
            return undefined;
          }
        })(),
        // Related data
        widgets: widgetsData,
        urls: urls,
        tags: tags
      };

      // Create JSON file and download
      const exportData = {
        version: '1.1',
        exported_at: new Date().toISOString(),
        workflows: [exportWorkflowData]
      };

      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (formData.title || wf?.title || 'workflow').replace(/[^a-zA-Z0-9-_]/g, '_');
      a.download = `workflow_${safeName}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      options?.onExportSuccess?.('Workflow exported successfully');
    } catch (error) {
      console.error('Error exporting workflow:', error);
      options?.onError?.(error instanceof Error ? error.message : 'Failed to export workflow');
    }
  }, [getToken, formData, workflow, options]);

  // Render modal helper
  const renderModal = useCallback((selectedLanguage: Language): React.ReactNode => {
    if (!isOpen || !workflow) return null;

    return React.createElement(EditWorkflowModal, {
      isOpen,
      onClose: close,
      onSave: handleSaveForModal,
      onExportWorkflow: exportWorkflow,
      workflow: workflow as BaseWorkflowWithTranslations,
      workflowWidgets: widgets,
      widgetTypes,
      selectedLanguage,
      saving,
      formData,
      onFormDataChange: handleFormDataChange,
      onWidgetToggle: toggleWidget,
      onDisplayOrderChange: updateDisplayOrder,
      onWidgetConfigChange: updateWidgetConfig,
      selectedCategoryIds,
      onCategoryIdsChange: setSelectedCategoryIds,
      selectedTagIds,
      onTagIdsChange: setSelectedTagIds
    });
  }, [isOpen, workflow, widgets, widgetTypes, saving, formData, selectedCategoryIds, selectedTagIds, close, handleSaveForModal, exportWorkflow, handleFormDataChange, toggleWidget, updateDisplayOrder, updateWidgetConfig]);

  return {
    isOpen,
    workflow,
    widgets,
    widgetTypes,
    formData,
    loading,
    saving,
    workflowId,
    open,
    close,
    save,
    updateField,
    toggleWidget,
    updateDisplayOrder,
    updateWidgetConfig,
    renderModal
  };
}
